/**
 * 幕二 · 右栏检视面板 —— 双态:没选镜 = 全片视图,选中 = 这一镜的全貌(2026-08-06 幕二重设计)。
 *
 * ═══ 为什么右栏要变成这个 ═══════════════════════════════════════════════════════
 * 真机实测(5 镜):页面 3112px 而右栏只有 648px —— **空转 79%**。而它的位置(sticky、
 * 常驻、眼睛正对)恰恰是最该放「我现在在看什么」的地方。左栏矮卡负责导航,右栏负责工作。
 *
 * ═══ ★这里才是「用户改动有数」的落点 ═══════════════════════════════════════════
 * 选中一镜时,显示**它所在那一段的真 prompt**(ai `compile_chunk_prompt` 的产物,
 * 与出片发的是同一条)。prompt 是**按段**的:相邻镜合 1~3 镜一段,改一镜影响的是整段。
 * 500 字是契约里唯一真硬的门,超了会截参考派活行 —— 所以预算条要露出来,
 * 用户正是靠它决定要不要精简,而不是等出片后发现被截。
 *
 * ★没选中镜时右栏不留白:切「全片视图」(拍法节拍 / 出场主体 / 全片素材 / 体检 / 积分 / 生成)。
 *   进幕二默认就是它 —— 先看整体,点任意一镜再看细节。
 * ★‹ › 逐镜翻:选中后注意力全程留在右栏,不用回左栏点下一镜(这是 master-detail 的老问题,
 *   加两个箭头就没了)。
 */
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Minus,
  Plus,
  RefreshCw,
  Star,
  Trash2,
  Upload,
} from "lucide-react";
import { useRef, useState, type ReactNode } from "react";

import { humanizeInternalTerms } from "@/shared/lib/internalCodes";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Textarea } from "@/shared/ui/textarea";
import { ratioToCss } from "@/shared/lib/video/aspect";
import { predictVoiceoverCap, voiceoverReadableSec } from "@/shared/lib/video/voiceoverCap";
import { VoiceoverCapHint } from "@/shared/ui/VoiceoverCapHint";
import {
  CAMERA_MOVES_LEX,
  CAMERA_MOVE_LABEL,
  CAMERA_SPEEDS_LEX,
  CAMERA_SPEED_LABEL,
  CONTINUITY_LEX,
  CONTINUITY_LABEL,
  LIGHTING_LEX,
  LIGHTING_LABEL,
  SHOT_SIZES_LEX,
  SHOT_SIZE_LABEL,
  VOICEOVER_DENSITY_MAX,
  voiceoverDensity,
} from "@/shared/lib/video/cameraLexicon";
import { IMAGE_ACCEPT } from "@/shared/lib/uploadImageFormats";
import { cn } from "@/shared/lib/utils";
import { shotSummary } from "../../model/shotSummary";
import {
  applyMotionPick,
  clearMotionPick,
  pickedMotion,
  pickedMotionLabel,
} from "../../model/motionPick";
import {
  describeShotReferences,
  resolveShotReferences,
  type RefCatalog,
} from "../../model/referenceThumbs";
import { MotionPickerDialog } from "./MotionPickerDialog";
import { segmentIndexOfShot } from "../../model/useCompilePreview";
import { PANEL_MOTION_MAX, PANEL_SCENE_MAX } from "../../model/buildTaskRequest";
import type { VideoPromptSegmentDto } from "../../api/videoService";
import type { PanelShotData } from "../../model/panelShot";
import { shotFactChipLabel } from "../../model/factChip";
import { LexPicker } from "./LexPicker";
import { ratioLabel, videoProviderLabel } from "@/shared/lib/video/labels";

/** 参考类别的短标(与 referenceThumbs 措辞同源,别各写一份)。 */
const REF_KIND_SHORT: Record<string, string> = { identity: "人", product: "品", motion: "动" };

/** W2 口播三态(导演产)——「确认展示不许比实际乐观」:有值就亮出来。 */
const SPEECH_MODE_LABEL: Record<string, string> = {
  voiceover: "旁白口播",
  dialogue: "角色对白",
  ambient: "无人声",
  lip_sync: "口型对齐",
};

/**
 * ★与 `buildTaskRequest` 的 PANEL_SCENE_MAX / PANEL_MOTION_MAX 同源,后者又对齐 ai
 * routes.py「每镜环境 / 动作」(0824 丰满化批放宽:动作可带表情/细节,
 * 编译预算已按镜数弹)。此前计数条分母与真上限不一致踩过坑(2026-08-07 走查):
 * 计数条、maxLength、组包钳三处必须同一个数。
 */
// VID-RICH-1(0901):直接 import 组包钳的单源常量 —— 此前这里手抄 60/90 与
// buildTaskRequest 各一份,而 ai 侧还是 44/30,三处漂两处;本批钉齐 90/64 且改单源。
const SCENE_MAX = PANEL_SCENE_MAX;
const MOTION_MAX = PANEL_MOTION_MAX;
const DURATION_MIN = 4;
const DURATION_MAX = 15;

interface Props {
  shots: PanelShotData[];
  ratio: string;
  selected: number | null;
  onSelect: (i: number | null) => void;
  onChangeShot: (i: number, next: PanelShotData) => void;
  onRemoveShot?: (i: number) => void;
  /** 在 index 处插一镜(index = 新镜落位)。★逐镜动作住在逐镜工作台,不占矮卡版面。 */
  onInsertShot?: (index: number) => void;
  /** 打开大图看图层(ShotLightbox 由页面持有,跨镜 ←/→ 才翻得动)。 */
  onPreview?: (i: number) => void;
  onRegenerateFrame?: (i: number) => void;
  onUploadFrame?: (i: number, f: File) => void;
  generatingSet?: ReadonlySet<number>;
  busy?: boolean;
  /** 真编译回来的段 + 预算(useCompilePreview)。 */
  segments: VideoPromptSegmentDto[];
  charBudget: number;
  previewLoading?: boolean;
  previewError?: string | null;
  /** 批 B1 素材目录:逐镜参考画成缩略图带(动作库/人物物料/产品图现签)。 */
  refCatalog?: RefCatalog;
  /** 编排时 AI 自动配了动作的镜(徽标判据;用户换/清后自然失效)。 */
  autoMotion?: boolean;
  /** 全片视图的插槽:拍法/主体/素材/体检/积分+生成 —— 由页面装配,本组件不去拿数据。 */
  overviewSlot?: ReactNode;
  /** W4:F 码 → 卖点原文(「这一镜在讲」块取文用);缺映射时退回显示 fid 本身。 */
  factTexts?: Record<string, string> | null;
  /** W4:当前口播语速(太稀提示的可念秒数估算用);缺省按 1 处理。 */
  voiceSpeechRate?: number | null;
}

function Sec({
  label,
  children,
  extra,
  className,
}: {
  label: string;
  children: ReactNode;
  extra?: ReactNode;
  /** 允许调用处排序(flex order)—— 见 VideoShotInspector 滚动体的 order 编排。 */
  className?: string;
}) {
  return (
    <div className={cn("border-b border-border/60 py-2.5 last:border-b-0", className)}>
      <div className="mb-2 flex items-center gap-2">
        {/* 段头竖色条 + 加粗深色标题:让每组可编辑项一眼分得清、不再一片灰(0908「太素」重设计) */}
        <span className="h-3.5 w-[3px] shrink-0 rounded-full bg-primary" aria-hidden />
        <span className="text-xs font-semibold text-foreground">{label}</span>
        {extra && <span className="ml-auto text-2xs text-muted-foreground">{extra}</span>}
      </div>
      {children}
    </div>
  );
}

/** 一段 prompt 的展示块:正文 + 字数预算条 + caveats。 */
function SegmentPrompt({
  seg,
  budget,
  shotIndex,
}: {
  seg: VideoPromptSegmentDto;
  budget: number;
  shotIndex: number;
}) {
  // 0824:预算按镜数弹,每段带自己的分母;老 ai 不回 charBudget 时退回响应级 500。
  const segBudget = seg.charBudget ?? budget;
  const pct = Math.min(100, Math.round((seg.chars / Math.max(1, segBudget)) * 100));
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-2xs text-muted-foreground">
        <span>
          第 {seg.seq} 段 · 镜 {seg.shotIndexes.map((x) => x + 1).join("、")}
        </span>
        {seg.shotIndexes.length > 1 && (
          <span className="text-primary/80">
            (本镜与另 {seg.shotIndexes.length - 1} 镜合成一段,共用这一条)
          </span>
        )}
        <span className="ml-auto font-mono">
          {seg.chars}/{segBudget}
        </span>
      </div>
      <div className="h-1 overflow-hidden rounded-full bg-border/60">
        <span
          className={cn("block h-full", seg.overBudget ? "bg-amber-500" : "bg-primary")}
          style={{ width: `${pct}%` }}
        />
      </div>
      <pre className="max-h-56 overflow-y-auto whitespace-pre-wrap rounded-md border border-border/50 bg-muted/30 px-2.5 py-2 text-2xs leading-relaxed text-foreground/90">
        {seg.prompt || "(这一段没编出内容)"}
      </pre>
      {seg.overBudget && (
        <p className="flex items-start gap-1 text-2xs text-amber-700 dark:text-amber-400">
          <AlertTriangle className="mt-px h-3 w-3 shrink-0" />
          超出 {segBudget} 字预算:出片时会按反 role 优先级截掉参考派活行。精简环境/动作可以救回来。
        </p>
      )}
      {seg.caveats?.map((c) => (
        <p key={c} className="text-2xs text-muted-foreground">
          · {humanizeInternalTerms(c)}
        </p>
      ))}
      {/* 实发参数行(0824):分镜面板里的景别/运镜是**编译原料**(经词典投影进上面的 prompt 正文),
          真正随 API 一起发的参数在这一行 —— 与渲染路同一批裁决,老 ai 不回这些字段时整行不渲染。 */}
      {seg.generationMode && (
        <p data-testid="segment-submit-params" className="font-mono text-2xs text-muted-foreground">
          实发参数:{seg.generationMode}
          {seg.resolution ? ` · ${seg.resolution}` : ""}
          {seg.ratio ? ` · ${ratioLabel(seg.ratio)}` : ""} · {seg.durationSec}s
          {(seg.imageRefCount ?? 0) > 0 ? ` · 参考图×${seg.imageRefCount}` : ""}
          {(seg.videoRefCount ?? 0) > 0 ? ` · 参考视频×${seg.videoRefCount}` : ""}
          {seg.hasAudioRef ? " · 口型音频×1" : ""} · seed 提交时按任务号派生
        </p>
      )}
      <p className="text-2xs text-muted-foreground/80">
        这是「{videoProviderLabel(seg.provider, seg.model)}
        」档实际会收到的那条(与出片同一份编译)。镜 {shotIndex + 1} 的改动会重算它。
      </p>
    </div>
  );
}

export function VideoShotInspector({
  shots,
  ratio,
  selected,
  onSelect,
  onChangeShot,
  onRemoveShot,
  onInsertShot,
  onPreview,
  onRegenerateFrame,
  onUploadFrame,
  generatingSet,
  busy,
  segments,
  charBudget,
  previewLoading,
  previewError,
  refCatalog,
  autoMotion,
  overviewSlot,
  factTexts,
  voiceSpeechRate,
}: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [motionOpen, setMotionOpen] = useState(false);
  const [negOpen, setNegOpen] = useState(false);

  // ── 全片视图(没选镜)──
  if (selected === null || !shots[selected]) {
    return (
      <aside
        data-video-shot-inspector
        data-mode="overview"
        className="overflow-hidden rounded-[calc(var(--radius)*1.15)] border border-border/60 bg-card/60"
      >
        <div className="flex items-center justify-between border-b border-border/60 px-3.5 py-2.5">
          <b className="text-[13px]">全片</b>
          <span className="text-2xs text-muted-foreground">点左边任一镜看细节</span>
        </div>
        <div className="px-3.5 py-1">{overviewSlot}</div>
      </aside>
    );
  }

  const i = selected;
  const s = shots[i];
  const patch = (next: Partial<PanelShotData>) => onChangeShot(i, { ...s, ...next });
  const gen = generatingSet?.has(i) || busy;
  const shown = s.firstFrameUrl || s.previewUrl;
  const isRenderFrame = !!s.firstFrameKey;
  const density = voiceoverDensity(s.voiceover, s.duration);
  const over = density > VOICEOVER_DENSITY_MAX;
  const voCut = predictVoiceoverCap(s.voiceover, s.duration);
  const summary = shotSummary({
    scene: s.scene,
    motion: s.motion,
    shotSize: s.shotSize,
    cameraMovement: s.cameraMovement,
    cameraSpeed: s.cameraSpeed,
    lightingKey: s.lightingKey,
    continuity: s.shotContinuity,
    heroMoment: s.heroMoment,
  });
  const segIdx = segmentIndexOfShot(segments, i);
  const resolvedRefs = resolveShotReferences(s.references, refCatalog ?? {});
  const isStatic = s.cameraMovement === "static";
  // W4:这一镜在讲 —— fact chip + 导演给的「任务」/「怎么拍」,三者全空则整块不显。
  const factChip = shotFactChipLabel(s.factRef, factTexts);
  const hasShotBrief = !!factChip || !!s.shotIntent || !!s.factVisual;
  // W4:太稀提示 —— 与超密红提示(voCut,7.28 硬裁)是同一块地皮的两侧,判据分开算。
  const readableSec = voiceoverReadableSec(s.voiceover, voiceSpeechRate);
  const sparseGapSec = s.duration - readableSec;
  const isSparse = readableSec < s.duration * 0.85;

  return (
    <aside
      data-video-shot-inspector
      data-mode="shot"
      className="overflow-hidden rounded-[calc(var(--radius)*1.15)] border border-border/60 bg-card/60"
    >
      {/* 头:‹ › 逐镜翻 + 秒数 + 删 —— 翻镜留在右栏,不用回左栏点 */}
      <div className="flex items-center justify-between gap-2 border-b border-border/60 px-3 py-2">
        <span className="flex items-center gap-1">
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-6 w-6"
            aria-label="上一镜"
            disabled={i === 0}
            onClick={() => onSelect(i - 1)}
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <b className="text-[13px]">
            镜 {i + 1} / {shots.length}
          </b>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-6 w-6"
            aria-label="下一镜"
            disabled={i === shots.length - 1}
            onClick={() => onSelect(i + 1)}
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
          {s.heroMoment && <Star className="h-3.5 w-3.5 fill-current text-amber-500" />}
          {s.speechMode && SPEECH_MODE_LABEL[s.speechMode] && (
            <span className="rounded border border-border/60 px-1 py-px text-2xs text-muted-foreground">
              {SPEECH_MODE_LABEL[s.speechMode]}
            </span>
          )}
        </span>
        <span className="flex items-center gap-0.5">
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-6 w-6"
            aria-label="本镜减 1 秒"
            disabled={gen || s.duration <= DURATION_MIN}
            onClick={() => patch({ duration: s.duration - 1 })}
          >
            <Minus className="h-3 w-3" />
          </Button>
          <span className="w-7 text-center font-mono text-2xs">{s.duration}s</span>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-6 w-6"
            aria-label="本镜加 1 秒"
            disabled={gen || s.duration >= DURATION_MAX}
            onClick={() => patch({ duration: s.duration + 1 })}
          >
            <Plus className="h-3 w-3" />
          </Button>
          {onRemoveShot && shots.length > 1 && (
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-6 w-6 text-muted-foreground hover:text-destructive"
              aria-label="删除本镜"
              disabled={gen}
              onClick={() => {
                onRemoveShot(i);
                onSelect(null);
              }}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </span>
      </div>

      {/* ★2026-09-08 老板「填环境/动作/景别/运镜还要下滑」:改成 flex 列 + order 编排,
          把**要编辑的**(环境/动作/口播/镜头语言)提到最前,选中一镜即见、不用滚;
          **只读的**台本图预览 + 发给模型提示词降到编辑项之后。DOM 顺序不动(order 纯视觉),
          既有按 label 取用的断言不受影响。 */}
      <div className="flex max-h-[calc(100dvh-8rem)] flex-col overflow-y-auto px-3.5 pb-3">
        {/* 预览图 + 身份(只读,排到编辑项之后) */}
        <div className="order-5 pt-3">
          {/* ★点图看大图:老卡的能力,不能跟着老卡一起没。看图层带跨镜 ←/→,
              而「对比连镜是否同一个人/同一个场」正是看大图的主要动机。 */}
          {shown && onPreview ? (
            <button
              type="button"
              aria-label="预览大图"
              onClick={() => onPreview(i)}
              className="mx-auto flex max-h-[240px] w-full cursor-zoom-in items-center justify-center overflow-hidden rounded-md border border-border/60 bg-muted/40 transition-colors hover:border-primary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              style={{ aspectRatio: ratioToCss(ratio, "9 / 16") }}
            >
              <img src={shown} alt={`镜 ${i + 1}`} className="h-full w-full object-contain" />
            </button>
          ) : (
            <div
              className="mx-auto flex max-h-[240px] items-center justify-center overflow-hidden rounded-md border border-border/60 bg-muted/40"
              style={{ aspectRatio: ratioToCss(ratio, "9 / 16") }}
            >
              {shown ? (
                <img src={shown} alt={`镜 ${i + 1}`} className="h-full w-full object-contain" />
              ) : (
                <span className="text-2xs text-muted-foreground">暂无台本图</span>
              )}
            </div>
          )}
          <p className="mt-1 text-center text-2xs text-muted-foreground">
            {isRenderFrame ? "起始画面 · 会用于出片" : "台本预览图 · 只给人看,不参与出片"}
          </p>
          <div className="mt-2 flex gap-1.5">
            {onRegenerateFrame && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 flex-1 gap-1 text-xs"
                disabled={gen}
                onClick={() => onRegenerateFrame(i)}
              >
                {gen ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <RefreshCw className="h-3 w-3" />
                )}
                重新生成
              </Button>
            )}
            {onUploadFrame && (
              <>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 flex-1 gap-1 text-xs"
                  disabled={gen}
                  onClick={() => fileRef.current?.click()}
                >
                  <Upload className="h-3 w-3" />
                  上传替换
                </Button>
                <input
                  ref={fileRef}
                  type="file"
                  accept={IMAGE_ACCEPT}
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) onUploadFrame(i, f);
                    e.target.value = "";
                  }}
                />
              </>
            )}
          </div>
        </div>

        {summary && (
          <Sec label="这一镜会画成" className="order-6">
            <p className="text-2xs leading-snug text-muted-foreground">{summary}</p>
          </Sec>
        )}

        {/* ★真 prompt —— 这一镜进的是哪一段、那一段长什么样 */}
        <Sec
          className="order-7"
          label="发给视频模型的提示词"
          extra={
            previewLoading ? (
              <span className="flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" />
                刷新中
              </span>
            ) : previewError ? (
              <span className="text-amber-600 dark:text-amber-400">刷新失败,下面是上一版</span>
            ) : undefined
          }
        >
          {segIdx >= 0 ? (
            <SegmentPrompt seg={segments[segIdx]} budget={charBudget} shotIndex={i} />
          ) : (
            <p className="text-2xs text-muted-foreground">
              {previewLoading ? "正在编译…" : "改完分镜后这里会显示这一镜实际会发出去的提示词。"}
            </p>
          )}
        </Sec>

        {/* W4:这一镜在讲 —— fact chip + 导演给的任务/怎么拍,三者全空则整块不显。放在「环境」
            块之前:改环境/动作之前先看清导演为什么安排这一镜。 */}
        {hasShotBrief && (
          <div
            data-testid="shot-inspector-fact-brief"
            className="order-0 mb-2.5 space-y-1 rounded-md border border-emerald-300/50 bg-emerald-50/60 px-2.5 py-2 text-2xs dark:border-emerald-400/30 dark:bg-emerald-950/20"
          >
            {factChip && (
              <span className="inline-block rounded-full border border-emerald-300/60 bg-background/60 px-1.5 py-px text-emerald-700 dark:text-emerald-300">
                {factChip}
              </span>
            )}
            {s.shotIntent && (
              <p className="text-emerald-800 dark:text-emerald-300">这一镜的任务:{s.shotIntent}</p>
            )}
            {s.factVisual && (
              <p className="text-emerald-800 dark:text-emerald-300">卖点怎么拍:{s.factVisual}</p>
            )}
          </div>
        )}

        <Sec
          label="环境(这一镜在哪儿)"
          extra={`${s.scene.length}/${SCENE_MAX}`}
          className="order-1"
        >
          <Textarea
            rows={3}
            value={s.scene}
            disabled={gen}
            maxLength={SCENE_MAX}
            onChange={(e) => patch({ scene: e.target.value })}
            className="border-border/80 text-[13px] leading-relaxed focus-visible:border-primary"
            placeholder="写环境、主体位置、光线氛围,如:明亮前台区,发光搜索框悬在桌面吸拢数字,暖光冷光在桌沿交汇"
          />
        </Sec>

        <Sec
          label="动作(主体在做什么)"
          extra={`${s.motion.length}/${MOTION_MAX}`}
          className="order-2"
        >
          <Textarea
            rows={3}
            value={s.motion}
            disabled={gen}
            maxLength={MOTION_MAX}
            onChange={(e) => patch({ motion: e.target.value })}
            className="border-border/80 text-[13px] leading-relaxed focus-visible:border-primary"
            placeholder="一个主动作写透,如:手猛地一挥,杂乱号码归拢成发光的 400,指尖落下轻点,屏幕被光环点亮"
          />
        </Sec>

        <Sec label="口播文案(burn-in 字幕)" className="order-3">
          <Input
            value={s.voiceover}
            disabled={gen}
            maxLength={60}
            onChange={(e) => patch({ voiceover: e.target.value })}
            className="h-8 text-[12.5px]"
            placeholder="15-25 字的短句"
          />
          <div className="mt-1 h-[3px] overflow-hidden rounded-full bg-border/60">
            <span
              className={cn("block h-full", over ? "bg-amber-500" : "bg-primary")}
              style={{ width: `${Math.min(100, (density / VOICEOVER_DENSITY_MAX) * 100)}%` }}
            />
          </div>
          <p
            className={cn(
              "mt-0.5 text-2xs",
              over ? "text-amber-700 dark:text-amber-400" : "text-muted-foreground"
            )}
          >
            {density.toFixed(1)} 字/秒{over ? "(超密,会念糊)" : ""} · 本镜 {s.duration}s 最多容{" "}
            {Math.floor(VOICEOVER_DENSITY_MAX * s.duration)} 字
          </p>
          {/* 尾句裁切明示(0817 拍板;1007 加强):常显「约可念 N 字 · 当前 M 字」,超了换警示色并
              逐句列出会被截掉的原文 + 怎么办 —— 判据与服务端同口径(先清洗再截断)。 */}
          <VoiceoverCapHint text={s.voiceover} durationSec={s.duration} subject="本镜" />
          {/* W4:太稀提示 —— 超密红提示的另一半。voCut 已经在说「太多念不完」,这条说「太少留白多」。 */}
          {!voCut.cut && isSparse && (
            <p
              data-testid="voiceover-sparse-hint"
              className="mt-0.5 text-2xs text-amber-700 dark:text-amber-400"
            >
              这镜念完约剩 {sparseGapSec.toFixed(1)}s 留白,可以再补几个字
            </p>
          )}
        </Sec>

        <Sec label="镜头语言" extra="不选 = AI 导演定" className="order-4">
          <div className="grid grid-cols-2 gap-1.5">
            <LexPicker
              title="景别"
              value={s.shotSize || undefined}
              valueLabel={s.shotSize ? (SHOT_SIZE_LABEL[s.shotSize] ?? s.shotSize) : undefined}
              options={SHOT_SIZES_LEX}
              disabled={gen}
              onSelect={(v) => patch({ shotSize: v ?? "" })}
            />
            <LexPicker
              title="运镜"
              value={s.cameraMovement || undefined}
              valueLabel={
                s.cameraMovement
                  ? (CAMERA_MOVE_LABEL[s.cameraMovement] ?? s.cameraMovement)
                  : undefined
              }
              groups={[
                { label: "稳", options: CAMERA_MOVES_LEX.filter((o) => o.tier === "safe") },
                { label: "进阶", options: CAMERA_MOVES_LEX.filter((o) => o.tier === "medium") },
                {
                  label: "高动态 · hook/高光镜专用",
                  options: CAMERA_MOVES_LEX.filter((o) => o.tier === "high"),
                },
              ]}
              disabled={gen}
              // static 是零运动,不是慢 —— 选它就把速度清掉(ai 编译层也不给固定机位配速度)
              onSelect={(v) =>
                patch({ cameraMovement: v ?? "", ...(v === "static" ? { cameraSpeed: "" } : null) })
              }
            />
            <LexPicker
              title="镜速"
              value={s.cameraSpeed || undefined}
              valueLabel={
                s.cameraSpeed ? (CAMERA_SPEED_LABEL[s.cameraSpeed] ?? s.cameraSpeed) : undefined
              }
              options={CAMERA_SPEEDS_LEX}
              disabled={gen || isStatic}
              note={isStatic ? "固定机位没有速度" : undefined}
              onSelect={(v) => patch({ cameraSpeed: v ?? "" })}
            />
            <LexPicker
              title="光"
              value={s.lightingKey || undefined}
              valueLabel={
                s.lightingKey ? (LIGHTING_LABEL[s.lightingKey] ?? s.lightingKey) : undefined
              }
              options={LIGHTING_LEX}
              disabled={gen}
              onSelect={(v) => patch({ lightingKey: v ?? "" })}
            />
            {i > 0 && (
              <LexPicker
                title="承接"
                value={s.shotContinuity || undefined}
                valueLabel={
                  s.shotContinuity
                    ? (CONTINUITY_LABEL[s.shotContinuity] ?? s.shotContinuity)
                    : undefined
                }
                options={CONTINUITY_LEX}
                disabled={gen}
                onSelect={(v) => patch({ shotContinuity: v ?? "" })}
              />
            )}
          </div>
        </Sec>

        {/* W3 动作库选用:挑动作 → 本镜 references[role=motion](ai 渲染层挂 reference_video)。 */}
        <Sec label="动作参考" className="order-8">
          <div className="flex flex-wrap items-center gap-1.5">
            <button
              type="button"
              disabled={gen}
              onClick={() => setMotionOpen(true)}
              className={cn(
                "cursor-pointer rounded-md border px-2 py-1 text-2xs transition-colors",
                gen
                  ? "cursor-not-allowed border-border/40 text-muted-foreground/50"
                  : pickedMotion(s)
                    ? "border-primary/40 bg-primary/5 text-foreground hover:border-primary"
                    : "border-dashed border-border/60 text-muted-foreground hover:border-primary/40 hover:text-foreground"
              )}
            >
              动作
              <span className={cn("ml-1", pickedMotion(s) ? "font-medium" : "")}>
                {pickedMotionLabel(pickedMotion(s)) ?? "不用参考"}
              </span>
            </button>
            {autoMotion && pickedMotion(s) && (
              <span
                title="编排时 AI 按镜位自动配的动作模板;可点「动作」换一条,或清除不用"
                className="rounded bg-primary/10 px-1 py-0.5 text-2xs text-primary"
              >
                AI 配
              </span>
            )}
            {pickedMotion(s) && (
              <button
                type="button"
                disabled={gen}
                onClick={() => onChangeShot(i, clearMotionPick(s))}
                className="cursor-pointer text-2xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
              >
                清除动作
              </button>
            )}
          </div>
        </Sec>

        {/* 批 B1:本镜挂了哪些参考(人物/产品/动作)—— 挂了什么当面摊开,不做黑箱。 */}
        {resolvedRefs.length > 0 && (
          <Sec label="本镜参考素材" className="order-9">
            <div className="flex flex-wrap gap-1.5">
              {resolvedRefs.map((ref, k) => (
                <span
                  key={k}
                  className="inline-flex items-center gap-1 rounded border border-border/60 bg-background/50 px-1.5 py-0.5"
                >
                  <span className="rounded bg-primary/15 px-1 text-2xs text-primary">
                    {REF_KIND_SHORT[ref.kind] ?? "参"}
                  </span>
                  <span className="max-w-[7rem] truncate text-2xs">{ref.name}</span>
                </span>
              ))}
            </div>
            {describeShotReferences(resolvedRefs) && (
              <p className="mt-1 text-2xs text-muted-foreground">
                {describeShotReferences(resolvedRefs)}
              </p>
            )}
          </Sec>
        )}

        <Sec label="负面词(透给视频模型,可编辑)" className="order-10">
          {negOpen ? (
            <Textarea
              rows={2}
              value={s.negativePrompt}
              disabled={gen}
              onChange={(e) => patch({ negativePrompt: e.target.value })}
              className="text-[12px]"
              placeholder="畸形、歪脸、模糊、低清、水印、变形手部..."
            />
          ) : (
            <button
              type="button"
              onClick={() => setNegOpen(true)}
              className="cursor-pointer text-2xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
            >
              {s.negativePrompt?.trim() ? "已自定义,点开编辑" : "已用默认,点开编辑"}
            </button>
          )}
        </Sec>

        {/* 逐镜动作:在这一镜后面插一镜。★放这儿而不是矮卡下 —— 老网格给每张卡下面挂了一条
            插镜条,矮卡本就以「压到 ~150px」为目的,再挂一条就把省下的高度还回去了。
            这里是「对这一镜」的工作台,插在它后面天然属于这儿。 */}
        {onInsertShot && (
          <div className="mt-3 border-t border-border/50 pt-2.5">
            <button
              type="button"
              disabled={gen}
              onClick={() => onInsertShot(i + 1)}
              className="flex h-7 w-full cursor-pointer items-center justify-center gap-1 rounded border border-dashed border-border/60 text-2xs text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Plus className="h-3 w-3" />
              在镜 {i + 1} 后插一镜
            </button>
          </div>
        )}
      </div>

      <MotionPickerDialog
        open={motionOpen}
        onOpenChange={setMotionOpen}
        onPick={(a) =>
          onChangeShot(
            i,
            applyMotionPick(s, { name: a.name, objectKey: a.objectKey, durationSec: a.durationSec })
          )
        }
      />
    </aside>
  );
}
